import { useState, useMemo, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTimeTracking, useTimeTrackingReport } from '@/hooks/useTimeTracking'
import { useProjects } from '@/hooks/useProjects'
import { useAuth } from '@/hooks/useAuth'
import { reportsService } from '@/services/reports'
import CustomDropdown from '@/components/CustomDropdown'
import DatePickerDropdown from '@/components/DatePickerDropdown'
import TimezoneIndicator from '@/components/TimezoneIndicator'
import SortableTableHeader, { SortConfig } from '@/components/SortableTableHeader'
import { useUserSettingsContext } from '@/hooks/useUserSettingsContext'
import { ReportSortColumn } from '../../../shared/types'
import {
  detectOverlappingEntries,
  checkOverlapWithBenefitOfDoubt
} from '@/utils/overlapDetection'
import {
  buildExportTimezoneOptions,
  getBrowserTimezone,
  PRESERVE_ORIGINAL
} from '@/utils/timezoneOptions'
import {
  getWeekKey as sharedGetWeekKey,
  getDateKeyInTimezone as sharedGetDateKeyInTimezone
} from '../../../shared/utils/dateUtils'
import { getLocaleForTimezone } from '../../../shared/utils/localeUtils'
import {
  calculateHourlyBudgetStatus,
  calculateMonetaryBudgetStatus,
  getBudgetTooltipText,
  getEffectiveHourlyRate,
  isExcludedBudgetDay,
  formatRate,
  aggregateRevenueByCurrency,
  formatRevenueSummary
} from '@/utils/budgetUtils'
import { BudgetStatus } from '../../../shared/types'
import ReportingDistributionChart from '@/components/ReportingDistributionChart'
import { useTimeEntriesCache } from '@/hooks/useTimeEntriesCache'

interface DateRange {
  startDate: string
  endDate: string
}

type ExportMode = 'task-based' | 'day-based'

interface ExportConfig {
  columns: {
    description: boolean
    project: boolean
    location: boolean
    startTime: boolean
    endTime: boolean
    duration: boolean
    pause: boolean
    day: boolean
    date: boolean
    originalTimezone: boolean
    rate: boolean    // Effective hourly rate (billable projects only)
    revenue: boolean // Calculated revenue (billable projects only)
  }
  summaries: {
    totalDay: boolean
    totalWeek: boolean
    total: boolean
  }
}

// Interface for aggregated day-based entries
interface DayBasedEntry {
  dateKey: string
  projectId: string | undefined
  projectName: string
  location: string
  description: string
  startTime: string
  endTime: string
  duration: number
  pause: number
  timezone: string | undefined
  hasOverlap: boolean // True if segments within this day/project overlap
  hasDifferentTimezone: boolean // True if any segment has a different timezone
  rate: number | null // Uniform effective rate across all segments (null if mixed or non-billable)
  revenue: number | null // Sum of per-segment revenues (null if non-billable)
  currency: string | null // ISO 4217 code (null if non-billable)
}

// Interface for entry segments after midnight splitting
interface EntrySegment {
  originalEntryId: string
  projectId: string | undefined
  location: string
  description: string
  timezone: string | undefined
  segmentStart: Date
  segmentEnd: Date
  segmentDuration: number // in seconds
  dateKey: string // YYYY-MM-DD in display timezone
}

export default function Reporting() {
  const { user } = useAuth()
  const timeTracking = useTimeTracking(!!user)
  const projects = useProjects()
  const { displaySettings, timezoneSettings, effectiveWeekStartDay } = useUserSettingsContext()
  const { allEntries: allChartEntries } = useTimeEntriesCache()

  // Only active projects — used for the project filter dropdown
  // (allProjects only contains active projects — archived ones are not loaded here)
  const activeProjects = useMemo(
    () => (projects.allProjects || []).filter(p => p.status === 'active' || !p.status),
    [projects.allProjects]
  )
  const [searchParams] = useSearchParams()

  // State for filters and configuration
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0'); // getMonth() is 0-indexed
    const day = today.getDate().toString().padStart(2, '0');
    
    return {
      startDate: `${year}-${month}-01`, // First of current month (YYYY-MM-01)
      endDate: `${year}-${month}-${day}` // Today (YYYY-MM-DD)
    };
  })
  
  // Helper functions to convert between Date objects and string format
  const getDateFromString = (dateStr: string): Date => new Date(dateStr + 'T00:00:00.000Z')
  const getStringFromDate = (date: Date): string => {
    // Extract local date components to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([])
  const [selectedLocation, setSelectedLocation] = useState<string>('')

  // Chart entries: unfiltered by date, but respect the project filter when active
  const chartEntries = useMemo(() => {
    if (selectedProjectIds.length === 0) return allChartEntries
    return allChartEntries.filter(e => e.projectId !== undefined && selectedProjectIds.includes(e.projectId))
  }, [allChartEntries, selectedProjectIds])

  // Sort configuration state (null = use default, which is date:desc)
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null)

  // Effective sort: user selection or default (date descending)
  const effectiveSort: SortConfig = useMemo(() =>
    sortConfig ?? { column: 'date', direction: 'desc' }
  , [sortConfig])

  // Handle sort column click - cycles through: desc -> asc -> none (default)
  const handleSort = useCallback((column: ReportSortColumn) => {
    setSortConfig(prev => {
      if (prev?.column !== column) {
        // New column - start with descending
        return { column, direction: 'desc' }
      }
      if (prev.direction === 'desc') {
        // Switch to ascending
        return { column, direction: 'asc' }
      }
      // Was ascending - go back to default (null)
      return null
    })
  }, [])

  // Use the new report-specific hook for date range filtering
  // Pass sort config in format "column:direction" (e.g., "date:desc", "duration:asc")
  const sortParam = `${effectiveSort.column}:${effectiveSort.direction}`
  const reportData = useTimeTrackingReport(
    dateRange,
    selectedProjectIds.length === 1 ? selectedProjectIds[0] : undefined,
    selectedLocation || undefined,
    sortParam,
    !!user
  )

  // Handle URL parameters for project filtering on initial load
  useEffect(() => {
    const projectParam = searchParams.get('project')
    if (projectParam) {
      setSelectedProjectIds([projectParam])
    }
  }, [searchParams]) // Added searchParams to dependency array

  const [exportMode, setExportMode] = useState<ExportMode>('task-based')

  const [exportConfig, setExportConfig] = useState<ExportConfig>({
    columns: {
      description: true,
      project: true,
      location: true,
      startTime: true,
      endTime: true,
      duration: true,
      pause: true,
      day: true,
      date: true,
      originalTimezone: false,
      rate: false,
      revenue: false
    },
    summaries: {
      totalDay: false,
      totalWeek: false,
      total: true
    }
  })

  // Export timezone state - defaults to user's preferred timezone or browser timezone
  const browserTimezone = getBrowserTimezone()
  const [exportTimezone, setExportTimezone] = useState<string>(() => {
    // Use preferred timezone if set, otherwise fall back to browser timezone
    return timezoneSettings.preferredTimezone || browserTimezone
  })
  const [isTimezoneExpanded, setIsTimezoneExpanded] = useState(false)
  const [isColumnsExpanded, setIsColumnsExpanded] = useState(false)
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false)
  const [hasInitializedTimezone, setHasInitializedTimezone] = useState(false)
  const [exportLanguage, setExportLanguage] = useState<'en' | 'de'>('en')

  const colLabels = exportLanguage === 'de'
    ? { day: 'Tag', date: 'Datum', startTime: 'Startzeit', endTime: 'Endzeit', duration: 'Dauer', pause: 'Pause', rate: 'Stundensatz', revenue: 'Betrag', project: 'Projekt', location: 'Standort', description: 'Beschreibung', originalTimezone: 'Urspr. Zeitzone' }
    : { day: 'Day', date: 'Date', startTime: 'Start Time', endTime: 'End Time', duration: 'Duration', pause: 'Pause', rate: 'Rate', revenue: 'Amount', project: 'Project', location: 'Location', description: 'Description', originalTimezone: 'Original TZ' }

  // Update exportTimezone when user's preferred timezone is loaded (only on initial load)
  useEffect(() => {
    if (!hasInitializedTimezone && timezoneSettings.preferredTimezone) {
      setExportTimezone(timezoneSettings.preferredTimezone)
      setHasInitializedTimezone(true)
    }
  }, [timezoneSettings.preferredTimezone, hasInitializedTimezone])

  // Build timezone options using shared utility
  const timezoneOptions = useMemo(() => {
    return buildExportTimezoneOptions(browserTimezone)
  }, [browserTimezone])

  // Derive locale from the selected export timezone.
  // When PRESERVE_ORIGINAL each entry has its own timezone, so fall back to
  // the user's preferred timezone (or browser timezone) as the locale source.
  const dateLocale = useMemo(() => {
    const tz = exportTimezone === PRESERVE_ORIGINAL
      ? (timezoneSettings.preferredTimezone || browserTimezone)
      : exportTimezone
    return getLocaleForTimezone(tz)
  }, [exportTimezone, timezoneSettings.preferredTimezone, browserTimezone])

  // Helper function to format time in a specific timezone
  const formatTimeInTimezone = useCallback((date: Date, timezone: string) => {
    return date.toLocaleTimeString(dateLocale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: timezone
    })
  }, [dateLocale])

  // Helper function to format date (day name) in a specific timezone
  const formatDayInTimezone = useCallback((date: Date, timezone: string) => {
    return date.toLocaleDateString(exportLanguage === 'de' ? 'de-DE' : 'en-US', {
      weekday: 'long',
      timeZone: timezone
    })
  }, [exportLanguage])

  // Helper function to format date number in a specific timezone
  const formatDateNumberInTimezone = useCallback((date: Date, timezone: string) => {
    return date.toLocaleDateString(dateLocale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: timezone
    })
  }, [dateLocale])

  const [isExporting, setIsExporting] = useState(false)

  // Use report loading and error states
  const isLoading = reportData.loading
  const error = reportData.error

  // Helper function to format duration
  const formatDuration = useCallback((seconds: number) => {
    const totalMinutes = Math.round(seconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }, []);


  // Helper function to get week key (YYYY-WNN format) in specified timezone
  // Delegates to shared utility for consistent client/server calculations
  const getWeekKey = useCallback((date: Date, timezone: string) => {
    return sharedGetWeekKey(date, timezone, effectiveWeekStartDay);
  }, [effectiveWeekStartDay]);

  // Helper function to get date key (YYYY-MM-DD format) in specified timezone
  // Delegates to shared utility for consistent client/server calculations
  const getDateKeyInTimezone = useCallback((date: Date, timezone: string): string => {
    return sharedGetDateKeyInTimezone(date, timezone);
  }, []);

  // Get the effective timezone for grouping calculations
  // When PRESERVE_ORIGINAL, each entry uses its own timezone; otherwise use export timezone
  const getEffectiveTimezone = useCallback((entryTimezone?: string): string => {
    if (exportTimezone === PRESERVE_ORIGINAL) {
      return entryTimezone || browserTimezone;
    }
    return exportTimezone;
  }, [exportTimezone, browserTimezone]);


  // Force preview update when export config changes
  useEffect(() => {
    // Preview will automatically update when exportConfig changes
  }, [exportConfig])

  // Auto-manage project/location/rate/revenue columns when filters are applied
  useEffect(() => {
    const hasBillableProjects = selectedProjectIds.length > 0
      ? selectedProjectIds.some(id => {
          const project = activeProjects.find(p => p.id === id)
          return project?.billable === true
        })
      : activeProjects.some(p => p.billable === true)

    setExportConfig(prev => ({
      ...prev,
      columns: {
        ...prev.columns,
        // Enable project column when multiple projects are selected, disable when single project
        project: selectedProjectIds.length > 1 ? true : (selectedProjectIds.length === 0 ? true : false),
        // Disable location column when location filter is applied
        location: selectedLocation && selectedLocation !== '' ? false : true,
        // Auto-enable rate/revenue when billable projects are in scope
        rate: hasBillableProjects,
        revenue: hasBillableProjects
      }
    }))
  }, [selectedProjectIds, selectedLocation, activeProjects])

  // Clear location when no project is selected
  useEffect(() => {
    if (!selectedProjectIds.length) {
      setSelectedLocation('')
    }
  }, [selectedProjectIds])



  // Helper to extract time-of-day in milliseconds (ignoring the date)
  // This allows sorting by just the time portion (e.g., 14:30 comes before 15:00 regardless of date)
  const getTimeOfDay = useCallback((isoString: string): number => {
    const date = new Date(isoString);
    return date.getUTCHours() * 3600000 + date.getUTCMinutes() * 60000 + date.getUTCSeconds() * 1000;
  }, []);

  // Active project IDs — report entries for projects no longer active are excluded
  const activeProjectIds = useMemo(
    () => new Set((projects.allProjects || []).map(p => p.id)),
    [projects.allProjects]
  )

  // Use report data for filtered entries (server-side filtering)
  // Apply client-side sorting to match user's sort selection
  const filteredEntries = useMemo(() => {
    // Keep entries with no project, or whose project is in the active set
    const entries = reportData.timeEntries.filter(
      entry => !entry.projectId || activeProjectIds.has(entry.projectId)
    );
    const multiplier = effectiveSort.direction === 'asc' ? 1 : -1;

    return entries.sort((a, b) => {
      let comparison = 0;

      switch (effectiveSort.column) {
        case 'date':
          // Sort by full timestamp for date column (chronological order)
          comparison = new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
          break;
        case 'startTime':
          // Sort by time-of-day only (e.g., 08:00 < 14:00 regardless of date)
          comparison = getTimeOfDay(a.startTime) - getTimeOfDay(b.startTime);
          break;
        case 'endTime': {
          // Sort by time-of-day only, entries without endTime go to the end/beginning
          const aEnd = a.endTime ? getTimeOfDay(a.endTime) : (effectiveSort.direction === 'desc' ? -1 : Infinity);
          const bEnd = b.endTime ? getTimeOfDay(b.endTime) : (effectiveSort.direction === 'desc' ? -1 : Infinity);
          comparison = aEnd - bEnd;
          break;
        }
        case 'duration': {
          // Calculate duration for each entry
          const aDuration = a.endTime
            ? new Date(a.endTime).getTime() - new Date(a.startTime).getTime()
            : Date.now() - new Date(a.startTime).getTime();
          const bDuration = b.endTime
            ? new Date(b.endTime).getTime() - new Date(b.startTime).getTime()
            : Date.now() - new Date(b.startTime).getTime();
          comparison = aDuration - bDuration;
          break;
        }
        case 'project': {
          // Get project names for comparison
          const aProject = projects.projects.find(p => p.id === a.projectId)?.name || '';
          const bProject = projects.projects.find(p => p.id === b.projectId)?.name || '';
          comparison = aProject.localeCompare(bProject);
          break;
        }
        case 'location':
          comparison = (a.location || '').localeCompare(b.location || '');
          break;
        case 'description':
          comparison = (a.description || '').localeCompare(b.description || '');
          break;
        case 'timezone':
          comparison = (a.timezone || '').localeCompare(b.timezone || '');
          break;
        case 'rate': {
          const aRateProject = a.projectId ? projects.projects.find(p => p.id === a.projectId) : undefined;
          const bRateProject = b.projectId ? projects.projects.find(p => p.id === b.projectId) : undefined;
          const aRate = aRateProject ? getEffectiveHourlyRate(aRateProject, a.location) : 0;
          const bRate = bRateProject ? getEffectiveHourlyRate(bRateProject, b.location) : 0;
          comparison = aRate - bRate;
          break;
        }
        case 'revenue': {
          const aProject = a.projectId ? projects.projects.find(p => p.id === a.projectId) : undefined;
          const bProject = b.projectId ? projects.projects.find(p => p.id === b.projectId) : undefined;
          const aDuration = a.endTime
            ? (new Date(a.endTime).getTime() - new Date(a.startTime).getTime()) / 1000
            : (Date.now() - new Date(a.startTime).getTime()) / 1000;
          const bDuration = b.endTime
            ? (new Date(b.endTime).getTime() - new Date(b.startTime).getTime()) / 1000
            : (Date.now() - new Date(b.startTime).getTime()) / 1000;
          const aRev = aProject?.billable ? (aDuration / 3600) * getEffectiveHourlyRate(aProject, a.location) : 0;
          const bRev = bProject?.billable ? (bDuration / 3600) * getEffectiveHourlyRate(bProject, b.location) : 0;
          comparison = aRev - bRev;
          break;
        }
        default:
          comparison = new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
      }

      return comparison * multiplier;
    });
  }, [reportData.timeEntries, activeProjectIds, effectiveSort, projects.projects, getTimeOfDay])


  // Get available locations from filtered entries
  const availableLocations = useMemo(() => {
    const locations = new Set<string>()
    filteredEntries.forEach(entry => {
      if (entry.location) {
        locations.add(entry.location)
      }
    })
    return Array.from(locations).sort()
  }, [filteredEntries])

  // Check if any entries have timezones different from browser timezone
  const hasDifferentTimezones = useMemo(() => {
    return filteredEntries.some(entry => entry.timezone && entry.timezone !== browserTimezone)
  }, [filteredEntries, browserTimezone])

  // Show timezone column only when user setting is enabled AND there are entries from different timezones
  const showTimezoneColumn = displaySettings.showTimezoneIndicator && hasDifferentTimezones

  // Detect overlapping entries for task-based view (within same project) using shared utility
  // Uses "benefit of doubt" approach: minute precision for form entries, second precision for timer entries
  const overlappingEntryIds = useMemo(() => {
    return detectOverlappingEntries(filteredEntries);
  }, [filteredEntries])

  // Calculate budget status per row for highlighting in preview and export.
  // Returns a Map keyed by:
  //   - task-based: entry.id
  //   - day-based:  `${dateKey}|${projectId ?? 'no-project'}`
  // NOTE: placed before dayBasedEntries so it can be used without circular deps.
  // The day-based path calls this memo again after dayBasedEntries is computed (see below).
  const budgetRowStatusTask = useMemo((): Map<string, { status: BudgetStatus; tooltipText: string }> => {
    if (!displaySettings.showBudgetWarnings || !filteredEntries.length) return new Map()

    const statusMap = new Map<string, { status: BudgetStatus; tooltipText: string }>()
    const projectMap = new Map(projects.projects.map(p => [p.id, p]))

    // --- Hourly budget: group entries by project+date, accumulate totals ---
    type HourlyGroup = { project: ReturnType<typeof projectMap.get> & object; entries: typeof filteredEntries; totalHours: number }
    const hourlyGroups = new Map<string, HourlyGroup>()
    for (const entry of filteredEntries) {
      const project = entry.projectId ? projectMap.get(entry.projectId) : undefined
      if (!project || project.budgetType !== 'hours') continue
      const entryTz = getEffectiveTimezone(entry.timezone)
      const dateKey = sharedGetDateKeyInTimezone(new Date(entry.startTime), entryTz)
      if (isExcludedBudgetDay(dateKey, project.budgetExcludedDays ?? [0, 6])) continue
      const key = `${entry.projectId}|${dateKey}`
      const hours = timeTracking.getCurrentDuration(entry) / 3600
      const existing = hourlyGroups.get(key)
      if (existing) {
        existing.entries.push(entry)
        existing.totalHours += hours
      } else {
        hourlyGroups.set(key, { project, entries: [entry], totalHours: hours })
      }
    }

    // Map each task entry → its day's budget status
    for (const { project, entries: group, totalHours } of hourlyGroups.values()) {
      const status = calculateHourlyBudgetStatus(project, totalHours)
      if (status === 'in') continue

      if (status === 'under') {
        // Under budget: flag only the last entry of the day (symmetric to crossover logic)
        const sorted = [...group].sort(
          (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
        )
        const last = sorted[sorted.length - 1]
        if (last) {
          statusMap.set(last.id, {
            status,
            tooltipText: getBudgetTooltipText(project, status, totalHours)
          })
        }
      } else {
        // Over budget: flag only the crossover entry (first to push running total past budget+tolerance)
        const sorted = [...group].sort(
          (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
        )
        const threshold = (project.budget ?? 0) + (project.budgetHourLimit ?? 60) / 60
        let running = 0
        let found = false
        for (const entry of sorted) {
          running += timeTracking.getCurrentDuration(entry) / 3600
          if (running > threshold) {
            statusMap.set(entry.id, {
              status,
              tooltipText: getBudgetTooltipText(project, status, totalHours)
            })
            found = true
            break
          }
        }
        if (!found && sorted.length > 0) {
          const last = sorted[sorted.length - 1]!
          statusMap.set(last.id, {
            status,
            tooltipText: getBudgetTooltipText(project, status, totalHours)
          })
        }
      }
    }

    // --- Monetary budget: accumulate cost chronologically per project ---
    const projectsWithMonetary = [...projectMap.values()].filter(p => p.budgetType === 'monetary' && p.budget)
    for (const project of projectsWithMonetary) {
      const projectEntries = filteredEntries
        .filter(e => e.projectId === project.id)
        .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
      let cumulative = 0
      for (const entry of projectEntries) {
        const hours = timeTracking.getCurrentDuration(entry) / 3600
        const rate = getEffectiveHourlyRate(project, entry.location)
        cumulative += hours * rate
        const status = calculateMonetaryBudgetStatus(project, cumulative)
        if (status === 'over') {
          statusMap.set(entry.id, {
            status: 'over',
            tooltipText: getBudgetTooltipText(project, 'over', undefined, cumulative)
          })
        }
      }
    }

    return statusMap
  }, [filteredEntries, projects.projects, displaySettings.showBudgetWarnings, timeTracking, getEffectiveTimezone])

  // Aggregate entries by day and project for day-based export
  // NOTE: This memo is intentionally placed BEFORE statistics so statistics can use it
  const dayBasedEntries = useMemo((): DayBasedEntry[] => {
    if (exportMode !== 'day-based' || !filteredEntries.length) return [];

    // Helper to get the effective timezone for an entry
    // When PRESERVE_ORIGINAL, use the entry's original timezone (or browser as fallback)
    // Otherwise use the selected export timezone
    const getEntryTimezone = (entryTimezone?: string): string => {
      if (exportTimezone === PRESERVE_ORIGINAL) {
        return entryTimezone || browserTimezone;
      }
      return exportTimezone;
    };

    // Helper to get date key (YYYY-MM-DD) in a specific timezone
    // Uses shared utility for consistency with server export
    const getDateKeyInTz = (date: Date, timezone: string): string => {
      return sharedGetDateKeyInTimezone(date, timezone);
    };

    // DST-aware: Get the exact midnight timestamp for the next day in a timezone
    // This correctly handles DST transitions where days can be 23 or 25 hours
    const getNextMidnightInTz = (d: Date, timezone: string): Date => {
      // Get the current date in the timezone as YYYY-MM-DD
      // Uses getDateKeyInTz for consistency with all date key calculations
      const currentDateKey = getDateKeyInTz(d, timezone);

      // Parse the date parts to create next day's midnight estimate
      const [year, month, day] = currentDateKey.split('-').map(Number);

      // Now we need to find the UTC time when it's midnight in the target timezone
      // Use a binary search approach to find the exact moment
      // Start with an estimate (local midnight for next day)
      const localMidnight = new Date(year, month - 1, day + 1, 0, 0, 0, 0);

      // Get the timezone offset by checking when the date changes in the target timezone
      // This correctly handles DST because the shared utility uses actual timezone rules
      let estimate = localMidnight.getTime();

      // Adjust by checking if we're at the right date
      let iterations = 0;
      const maxIterations = 48; // Safety limit (max 48 hours offset possible)

      while (iterations < maxIterations) {
        const testDate = new Date(estimate);
        const testDateKey = getDateKeyInTz(testDate, timezone);

        if (testDateKey === currentDateKey) {
          // Still on current day, move forward
          estimate += 3600000; // +1 hour
        } else {
          // We've passed midnight, now find exact moment
          // Binary search between estimate-1hour and estimate
          let low = estimate - 3600000;
          let high = estimate;

          while (high - low > 1) {
            const mid = Math.floor((low + high) / 2);
            const midDateKey = getDateKeyInTz(new Date(mid), timezone);

            if (midDateKey === currentDateKey) {
              low = mid;
            } else {
              high = mid;
            }
          }

          return new Date(high);
        }
        iterations++;
      }

      // Fallback: return estimate (shouldn't reach here)
      return new Date(estimate);
    };

    // Split an entry at midnight boundaries using the entry's effective timezone
    // When PRESERVE_ORIGINAL is selected, each entry uses its own timezone for splitting
    const splitEntryAtMidnight = (entry: typeof filteredEntries[0]): EntrySegment[] => {
      const segments: EntrySegment[] = [];
      const start = new Date(entry.startTime);
      const end = entry.endTime ? new Date(entry.endTime) : new Date();

      // Use the entry's effective timezone for date calculations
      const entryTz = getEntryTimezone(entry.timezone);

      const startDateKey = getDateKeyInTz(start, entryTz);
      const endDateKey = getDateKeyInTz(end, entryTz);

      // If same day, no splitting needed
      if (startDateKey === endDateKey) {
        const duration = (end.getTime() - start.getTime()) / 1000;
        return [{
          originalEntryId: entry.id,
          projectId: entry.projectId,
          location: entry.location || '',
          description: entry.description || '',
          timezone: entry.timezone,
          segmentStart: start,
          segmentEnd: end,
          segmentDuration: Math.max(0, duration),
          dateKey: startDateKey
        }];
      }

      // Entry spans multiple days - split at each midnight (DST-aware)
      // Use the entry's effective timezone for midnight boundaries
      let currentStart = start;
      let currentDateKey = startDateKey;

      while (currentDateKey !== endDateKey) {
        // Get the exact midnight time for the next day (DST-aware)
        // Use the entry's timezone so midnight is at the correct local time
        const midnightTime = getNextMidnightInTz(currentStart, entryTz);

        // Segment ends at 23:59:59.999 (1ms before midnight)
        const segmentEnd = new Date(midnightTime.getTime() - 1);
        const segmentDuration = (segmentEnd.getTime() - currentStart.getTime()) / 1000;

        segments.push({
          originalEntryId: entry.id,
          projectId: entry.projectId,
          location: entry.location || '',
          description: entry.description || '',
          timezone: entry.timezone,
          segmentStart: currentStart,
          segmentEnd: segmentEnd,
          segmentDuration: Math.max(0, segmentDuration),
          dateKey: currentDateKey
        });

        // Move to next day (start at midnight)
        currentStart = midnightTime;
        currentDateKey = getDateKeyInTz(currentStart, entryTz);
      }

      // Add the final segment (on the end date)
      const finalDuration = (end.getTime() - currentStart.getTime()) / 1000;
      segments.push({
        originalEntryId: entry.id,
        projectId: entry.projectId,
        location: entry.location || '',
        description: entry.description || '',
        timezone: entry.timezone,
        segmentStart: currentStart,
        segmentEnd: end,
        segmentDuration: Math.max(0, finalDuration),
        dateKey: currentDateKey
      });

      return segments;
    };

    // Split all entries at midnight boundaries
    const allSegments: EntrySegment[] = [];
    for (const entry of filteredEntries) {
      const segments = splitEntryAtMidnight(entry);
      allSegments.push(...segments);
    }

    // Group segments by day and project
    const groups = new Map<string, EntrySegment[]>();

    for (const segment of allSegments) {
      const projectKey = segment.projectId || 'no-project';
      const groupKey = `${segment.dateKey}|${projectKey}`;

      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(segment);
    }

    // Convert groups to aggregated entries
    const aggregatedEntries: DayBasedEntry[] = [];

    for (const [groupKey, groupSegments] of groups) {
      const [dateKey, projectKey] = groupKey.split('|');

      // Sort segments within group by start time (earliest first)
      const sortedSegments = [...groupSegments].sort((a, b) =>
        a.segmentStart.getTime() - b.segmentStart.getTime()
      );

      // Calculate pause (idle time between segments) using "high-water mark" approach
      // This correctly handles nested overlapping entries (e.g., short entry B within long entry A)
      // by tracking the maximum end time seen so far, not just the previous segment's end
      // Pause is 0 if there's only one segment
      // Only count positive gaps (not overlaps)

      let totalPause = 0;
      let hasOverlap = false;

      // Revenue tracking across all segments in this group
      let groupRevenue = 0
      let groupHasRevenue = false
      let groupUniformRate: number | null = null
      let groupRateIsUniform = true
      let groupCurrency: string | null = null

      // Calculate revenue contribution per segment (before pause/overlap math)
      for (const seg of sortedSegments) {
        const segProject = seg.projectId ? projects.projects.find(p => p.id === seg.projectId) : undefined
        if (segProject?.billable) {
          const segRate = getEffectiveHourlyRate(segProject, seg.location || undefined)
          const segRevenue = (seg.segmentDuration / 3600) * segRate
          groupRevenue += segRevenue
          groupHasRevenue = true
          groupCurrency = segProject.currency ?? displaySettings.defaultCurrency
          if (groupUniformRate === null) {
            groupUniformRate = segRate
          } else if (groupUniformRate !== segRate) {
            groupRateIsUniform = false
          }
        }
      }

      if (sortedSegments.length > 1) {
        // Track the maximum end time seen so far (high-water mark)
        let maxEndSoFar = sortedSegments[0].segmentEnd.getTime();

        for (let i = 1; i < sortedSegments.length; i++) {
          const currStart = sortedSegments[i].segmentStart;
          const currEnd = sortedSegments[i].segmentEnd;

          // Gap is from max end seen so far to current start
          // This correctly handles nested entries where a short entry ends before a longer one
          const gap = (currStart.getTime() - maxEndSoFar) / 1000;
          if (gap > 0) {
            totalPause += gap;
          }

          // Use shared utility for overlap detection with "benefit of doubt"
          // Compare against max end seen so far, not just previous segment
          if (checkOverlapWithBenefitOfDoubt(new Date(maxEndSoFar), currStart)) {
            hasOverlap = true;
          }

          // Update high-water mark
          maxEndSoFar = Math.max(maxEndSoFar, currEnd.getTime());
        }
      }

      // Check if any segment has a different timezone than browser
      // Use 'Europe/Berlin' as default for legacy entries without timezone (consistent with TimezoneIndicator component)
      const DEFAULT_TIMEZONE = 'Europe/Berlin';
      const hasDifferentTimezone = sortedSegments.some(
        seg => (seg.timezone || DEFAULT_TIMEZONE) !== browserTimezone
      );

      // Find a segment with a different timezone to use for display
      // This ensures the indicator shows even if the first segment has no timezone
      const differentTimezoneSegment = sortedSegments.find(
        seg => (seg.timezone || DEFAULT_TIMEZONE) !== browserTimezone
      );

      // Calculate total duration as timeline coverage (handles overlapping entries)
      // Duration = (Max End - First Start) - Pause
      // This correctly handles overlapping entries by using the actual time span
      // NOTE: We use Math.max for lastEnd because segments sorted by start time may not
      // have monotonic end times (e.g., a long entry A: 09:00-17:00 followed by short B: 10:00-12:00)
      const firstStart = sortedSegments[0].segmentStart.getTime();
      const lastEnd = Math.max(...sortedSegments.map(s => s.segmentEnd.getTime()));
      const totalDuration = ((lastEnd - firstStart) / 1000) - totalPause;

      // Get first segment for start time, find segment with max end time for end time
      const firstSegment = sortedSegments[0];

      // Concatenate descriptions:
      // Split each entry description by comma first (users sometimes type multiple tasks
      // in one description field, e.g. "technical setup, daily"), then deduplicate
      // case-insensitively across all segments, preserving first-occurrence casing.
      const seenDescKeys = new Set<string>();
      const descriptions: string[] = [];
      for (const seg of sortedSegments) {
        const parts = seg.description.split(',').map(p => p.trim()).filter(Boolean);
        for (const part of parts) {
          const key = part.toLowerCase();
          if (!seenDescKeys.has(key)) {
            seenDescKeys.add(key);
            descriptions.push(part);
          }
        }
      }

      // Concatenate locations (unique only, case-insensitive, preserving first-occurrence casing)
      const seenLocKeys = new Set<string>();
      const locations: string[] = [];
      for (const seg of sortedSegments) {
        const trimmed = seg.location.trim();
        if (!trimmed) continue;
        const key = trimmed.toLowerCase();
        if (!seenLocKeys.has(key)) {
          seenLocKeys.add(key);
          locations.push(trimmed);
        }
      }

      // Get project name
      const project = projectKey !== 'no-project'
        ? projects.projects.find(p => p.id === projectKey)
        : null;

      aggregatedEntries.push({
        dateKey,
        projectId: projectKey !== 'no-project' ? projectKey : undefined,
        projectName: project ? project.name : '-',
        location: locations.length > 0 ? locations.join(', ') : '-',
        description: descriptions.join(', '),
        startTime: firstSegment.segmentStart.toISOString(),
        endTime: new Date(lastEnd).toISOString(),
        duration: totalDuration,
        pause: totalPause,
        // Use the timezone of a segment that differs from browser, if any, to ensure indicator displays correctly
        timezone: differentTimezoneSegment?.timezone || firstSegment.timezone,
        hasOverlap,
        hasDifferentTimezone,
        rate: groupHasRevenue && groupRateIsUniform ? groupUniformRate : null,
        revenue: groupHasRevenue ? groupRevenue : null,
        currency: groupHasRevenue ? groupCurrency : null
      });
    }

    // Sort based on user's sort selection
    const multiplier = effectiveSort.direction === 'asc' ? 1 : -1;
    aggregatedEntries.sort((a, b) => {
      let comparison = 0;

      switch (effectiveSort.column) {
        case 'date':
          comparison = a.dateKey.localeCompare(b.dateKey);
          break;
        case 'startTime':
          // Sort by time-of-day only
          comparison = getTimeOfDay(a.startTime) - getTimeOfDay(b.startTime);
          break;
        case 'endTime':
          // Sort by time-of-day only
          comparison = getTimeOfDay(a.endTime) - getTimeOfDay(b.endTime);
          break;
        case 'duration':
          comparison = a.duration - b.duration;
          break;
        case 'pause':
          comparison = a.pause - b.pause;
          break;
        case 'project':
          comparison = a.projectName.localeCompare(b.projectName);
          break;
        case 'location':
          comparison = a.location.localeCompare(b.location);
          break;
        case 'description':
          comparison = a.description.localeCompare(b.description);
          break;
        case 'timezone':
          comparison = (a.timezone || '').localeCompare(b.timezone || '');
          break;
        case 'rate':
          comparison = (a.rate ?? 0) - (b.rate ?? 0);
          break;
        case 'revenue':
          comparison = (a.revenue ?? 0) - (b.revenue ?? 0);
          break;
        default:
          comparison = a.dateKey.localeCompare(b.dateKey);
      }

      return comparison * multiplier;
    });

    return aggregatedEntries;
  }, [exportMode, filteredEntries, exportTimezone, browserTimezone, projects.projects, effectiveSort, getTimeOfDay, displaySettings.defaultCurrency]);

  // Budget row status for day-based mode — computed after dayBasedEntries is available
  const budgetRowStatusDay = useMemo((): Map<string, { status: BudgetStatus; tooltipText: string }> => {
    if (!displaySettings.showBudgetWarnings || exportMode !== 'day-based' || !dayBasedEntries.length) return new Map()

    const statusMap = new Map<string, { status: BudgetStatus; tooltipText: string }>()
    const projectMap = new Map(projects.projects.map(p => [p.id, p]))

    // --- Hourly budget: each dayBasedEntry is already a per-day aggregate ---
    for (const entry of dayBasedEntries) {
      const project = entry.projectId ? projectMap.get(entry.projectId) : undefined
      if (!project || project.budgetType !== 'hours') continue
      if (isExcludedBudgetDay(entry.dateKey, project.budgetExcludedDays ?? [0, 6])) continue
      const hoursWorked = entry.duration / 3600
      const status = calculateHourlyBudgetStatus(project, hoursWorked)
      if (status !== 'in') {
        const rowKey = `${entry.dateKey}|${entry.projectId ?? 'no-project'}`
        statusMap.set(rowKey, {
          status,
          tooltipText: getBudgetTooltipText(project, status, hoursWorked)
        })
      }
    }

    // --- Monetary budget: accumulate cost chronologically per project ---
    const projectsWithMonetary = [...projectMap.values()].filter(p => p.budgetType === 'monetary' && p.budget)
    for (const project of projectsWithMonetary) {
      const projectDayEntries = dayBasedEntries
        .filter(e => e.projectId === project.id)
        .sort((a, b) => a.dateKey.localeCompare(b.dateKey))
      let cumulative = 0
      for (const entry of projectDayEntries) {
        const hours = entry.duration / 3600
        const rate = getEffectiveHourlyRate(project, entry.location)
        cumulative += hours * rate
        const status = calculateMonetaryBudgetStatus(project, cumulative)
        if (status === 'over') {
          const rowKey = `${entry.dateKey}|${entry.projectId ?? 'no-project'}`
          statusMap.set(rowKey, {
            status: 'over',
            tooltipText: getBudgetTooltipText(project, 'over', undefined, cumulative)
          })
        }
      }
    }

    return statusMap
  }, [dayBasedEntries, projects.projects, displaySettings.showBudgetWarnings, exportMode])

  // Unified budget status map for use in rendering (task or day based on current export mode)
  const budgetRowStatus = exportMode === 'day-based' ? budgetRowStatusDay : budgetRowStatusTask

  // Calculate statistics based on what's actually shown in preview
  // In day-based mode, use dayBasedEntries directly to ensure consistency with Day Preview
  const statistics = useMemo(() => {
    if (!filteredEntries.length) {
      return {
        totalEntries: 0,
        totalDuration: 0,
        totalHours: 0,
        averageHoursPerDay: 0,
        daysWithEntries: 0,
        displayLabel: 'Total Entries'
      }
    }

    let totalDuration: number;
    let uniqueDays: number;

    if (exportMode === 'day-based' && dayBasedEntries.length > 0) {
      // In day-based mode, use dayBasedEntries directly for consistency with Day Preview
      // This ensures statistics match exactly what's shown in the preview table
      // Note: dayBasedEntries already uses entry's timezone for PRESERVE_ORIGINAL

      // Total duration: sum of day-based entry durations (handles overlaps correctly via timeline coverage)
      totalDuration = dayBasedEntries.reduce((sum, e) => sum + e.duration, 0);

      // Unique days: count unique dateKeys from dayBasedEntries (DST-aware midnight splitting)
      uniqueDays = new Set(dayBasedEntries.map(e => e.dateKey)).size;
    } else {
      // Task-based mode: use individual entry calculations
      // Total duration: sum of individual entry durations
      totalDuration = filteredEntries.reduce((sum, entry) => {
        return sum + timeTracking.getCurrentDuration(entry)
      }, 0);

      // Unique days: count by start date using each entry's effective timezone
      // When PRESERVE_ORIGINAL, each entry uses its own timezone for date calculation
      // Uses shared utility for consistency with server export
      uniqueDays = new Set(filteredEntries.map(entry => {
        const date = new Date(entry.startTime);
        // Use entry's timezone for PRESERVE_ORIGINAL, otherwise use export timezone
        const entryTz = exportTimezone === PRESERVE_ORIGINAL
          ? (entry.timezone || browserTimezone)
          : exportTimezone;
        return sharedGetDateKeyInTimezone(date, entryTz);
      })).size;
    }

    const totalHours = totalDuration / 3600;

    return {
      totalEntries: filteredEntries.length,
      totalDuration,
      totalHours: Math.round(totalHours * 100) / 100,
      averageHoursPerDay: uniqueDays > 0 ? Math.round((totalHours / uniqueDays) * 100) / 100 : 0,
      daysWithEntries: uniqueDays,
      displayLabel: 'Total Entries'
    }
  }, [filteredEntries, timeTracking, exportTimezone, browserTimezone, exportMode, dayBasedEntries])

  // Generate day-based preview data with summaries (day, week, total)
  const dayBasedPreviewData = useMemo(() => {
    if (exportMode !== 'day-based' || !dayBasedEntries.length) return [];

    type DayBasedPreviewItem =
      | { type: 'entry'; entry: DayBasedEntry }
      | { type: 'summary'; summary: { summaryType: 'day' | 'week' | 'grand'; duration: number; pause: number; revenueSummary: string } };

    const data: DayBasedPreviewItem[] = [];
    const entriesByDay = new Map<string, DayBasedEntry[]>();
    const entriesByWeek = new Map<string, DayBasedEntry[]>();
    let currentWeek = "";
    let lastDate = "";

    // Process entries and add summaries
    for (const entry of dayBasedEntries) {
      const dateKey = entry.dateKey;
      // Use timezone-aware week grouping to match server export
      const entryTz = getEffectiveTimezone(entry.timezone);
      const weekKey = getWeekKey(new Date(entry.startTime), entryTz);

      // Group by day
      if (!entriesByDay.has(dateKey)) {
        entriesByDay.set(dateKey, []);
      }
      entriesByDay.get(dateKey)!.push(entry);

      // Group by week
      if (!entriesByWeek.has(weekKey)) {
        entriesByWeek.set(weekKey, []);
      }
      entriesByWeek.get(weekKey)!.push(entry);

      // Add day summary when day changes (if enabled and duration > 0)
      if (exportConfig.summaries.totalDay && lastDate && dateKey !== lastDate) {
        const dayEntries = entriesByDay.get(lastDate) || [];
        const dayDuration = dayEntries.reduce((sum, e) => sum + e.duration, 0);
        const dayPause = dayEntries.reduce((sum, e) => sum + e.pause, 0);
        if (dayDuration > 0) {
          data.push({
            type: 'summary',
            summary: { summaryType: 'day', duration: dayDuration, pause: dayPause, revenueSummary: formatRevenueSummary(aggregateRevenueByCurrency(dayEntries)) }
          });
        }
      }

      // Add week summary when week changes (if enabled and duration > 0)
      if (exportConfig.summaries.totalWeek && currentWeek && weekKey !== currentWeek) {
        const weekEntries = entriesByWeek.get(currentWeek) || [];
        const weekDuration = weekEntries.reduce((sum, e) => sum + e.duration, 0);
        const weekPause = weekEntries.reduce((sum, e) => sum + e.pause, 0);
        if (weekDuration > 0) {
          data.push({
            type: 'summary',
            summary: { summaryType: 'week', duration: weekDuration, pause: weekPause, revenueSummary: formatRevenueSummary(aggregateRevenueByCurrency(weekEntries)) }
          });
        }
      }

      // Add entry
      data.push({ type: 'entry', entry });

      currentWeek = weekKey;
      lastDate = dateKey;
    }

    // Add final day summary (if enabled and duration > 0)
    if (exportConfig.summaries.totalDay && lastDate) {
      const dayEntries = entriesByDay.get(lastDate) || [];
      const dayDuration = dayEntries.reduce((sum, e) => sum + e.duration, 0);
      const dayPause = dayEntries.reduce((sum, e) => sum + e.pause, 0);
      if (dayDuration > 0) {
        data.push({
          type: 'summary',
          summary: { summaryType: 'day', duration: dayDuration, pause: dayPause, revenueSummary: formatRevenueSummary(aggregateRevenueByCurrency(dayEntries)) }
        });
      }
    }

    // Add final week summary (if enabled and duration > 0)
    if (exportConfig.summaries.totalWeek && currentWeek) {
      const weekEntries = entriesByWeek.get(currentWeek) || [];
      const weekDuration = weekEntries.reduce((sum, e) => sum + e.duration, 0);
      const weekPause = weekEntries.reduce((sum, e) => sum + e.pause, 0);
      if (weekDuration > 0) {
        data.push({
          type: 'summary',
          summary: { summaryType: 'week', duration: weekDuration, pause: weekPause, revenueSummary: formatRevenueSummary(aggregateRevenueByCurrency(weekEntries)) }
        });
      }
    }

    // Add grand total (if enabled and duration > 0)
    if (exportConfig.summaries.total) {
      const totalDuration = dayBasedEntries.reduce((sum, e) => sum + e.duration, 0);
      const totalPause = dayBasedEntries.reduce((sum, e) => sum + e.pause, 0);
      if (totalDuration > 0) {
        data.push({
          type: 'summary',
          summary: { summaryType: 'grand', duration: totalDuration, pause: totalPause, revenueSummary: formatRevenueSummary(aggregateRevenueByCurrency(dayBasedEntries)) }
        });
      }
    }

    return data;
  }, [exportMode, dayBasedEntries, exportConfig.summaries, getWeekKey, getEffectiveTimezone]);

  // Pre-process entries for task-based export (WYSIWYG consistency)
  // This ensures the exported Excel matches exactly what's shown in the preview
  const taskBasedExportEntries = useMemo(() => {
    if (exportMode !== 'task-based' || !filteredEntries.length) return [];

    // Helper to format time for display (HH:MM in 24h format)
    const formatTimeForExport = (isoString: string, timezone: string): string => {
      const date = new Date(isoString);
      return date.toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: timezone
      });
    };

    // Helper to get day name (e.g., "Monday" / "Montag")
    const getDayName = (date: Date, timezone: string): string => {
      return date.toLocaleDateString(exportLanguage === 'de' ? 'de-DE' : 'en-US', {
        weekday: 'long',
        timeZone: timezone
      });
    };

    // Create a map of project IDs for efficient lookup
    const projectMap = new Map(projects.projects.map(p => [p.id, p]));

    return filteredEntries.map(entry => {
      const start = new Date(entry.startTime);
      const entryTz = getEffectiveTimezone(entry.timezone);
      const duration = timeTracking.getCurrentDuration(entry);

      // Revenue calculation: only for billable projects
      let rate: number | null = null
      let revenue: number | null = null
      let currency: string | null = null
      const project = entry.projectId ? projectMap.get(entry.projectId) : undefined
      if (project?.billable) {
        rate = getEffectiveHourlyRate(project, entry.location ?? undefined)
        revenue = (duration / 3600) * rate
        currency = project.currency ?? displaySettings.defaultCurrency
      }

      return {
        id: entry.id,
        dateKey: getDateKeyInTimezone(start, entryTz),
        weekKey: getWeekKey(start, entryTz),
        dayName: getDayName(start, entryTz),
        projectId: entry.projectId,
        projectName: entry.projectId ? (projectMap.get(entry.projectId)?.name || '-') : '-',
        location: entry.location || '-',
        description: entry.description || '',
        startTime: entry.startTime,
        endTime: entry.endTime,
        formattedStartTime: formatTimeForExport(entry.startTime, entryTz),
        formattedEndTime: entry.endTime ? formatTimeForExport(entry.endTime, entryTz) : '-',
        duration,
        timezone: entry.timezone,
        hasOverlap: overlappingEntryIds.has(entry.id),
        rate,
        revenue,
        currency
      };
    });
  }, [exportMode, filteredEntries, projects.projects, timeTracking, getEffectiveTimezone, getDateKeyInTimezone, getWeekKey, overlappingEntryIds, displaySettings.defaultCurrency, exportLanguage]);

  // Generate preview data with summaries
  const previewData = useMemo(() => {
    if (!filteredEntries.length) return [];

    const data: Array<{ type: 'entry' | 'summary', entry?: typeof filteredEntries[0], summary?: { summaryType: 'day' | 'week' | 'grand', duration: number, revenueSummary: string } }> = [];

    // Build a revenue lookup map from task-based export entries (revenue is pre-computed there)
    const revenueByEntryId = new Map<string, { revenue: number | null; currency: string | null }>(
      taskBasedExportEntries.map(e => [e.id, { revenue: e.revenue, currency: e.currency }])
    )

    type GroupEntry = { duration: number; revenue: number | null; currency: string | null }
    const entriesByDay = new Map<string, Array<GroupEntry>>();
    const entriesByWeek = new Map<string, Array<GroupEntry>>();
    let currentWeek = "";
    let lastDate = "";

    // Process entries and add summaries
    for (const entry of filteredEntries) {
      const start = new Date(entry.startTime);
      const duration = timeTracking.getCurrentDuration(entry);
      // Use timezone-aware date/week grouping to match server export
      const entryTz = getEffectiveTimezone(entry.timezone);
      const dateKey = getDateKeyInTimezone(start, entryTz);
      const weekKey = getWeekKey(start, entryTz);
      const revenueInfo = revenueByEntryId.get(entry.id)

      // Group by day
      if (!entriesByDay.has(dateKey)) {
        entriesByDay.set(dateKey, []);
      }
      const dayEntries = entriesByDay.get(dateKey);
      if (dayEntries) {
        dayEntries.push({ duration, revenue: revenueInfo?.revenue ?? null, currency: revenueInfo?.currency ?? null });
      }

      // Group by week
      if (!entriesByWeek.has(weekKey)) {
        entriesByWeek.set(weekKey, []);
      }
      const weekEntries = entriesByWeek.get(weekKey);
      if (weekEntries) {
        weekEntries.push({ duration, revenue: revenueInfo?.revenue ?? null, currency: revenueInfo?.currency ?? null });
      }

      // Add day summary if enabled and day changed
      if (exportConfig.summaries.totalDay && lastDate && lastDate !== dateKey) {
        const dayEntriesForSummary = entriesByDay.get(lastDate) || [];
        const dayTotal = dayEntriesForSummary.reduce((sum, e) => sum + e.duration, 0);
        if (dayTotal > 0) {
          data.push({
            type: 'summary',
            summary: {
              summaryType: 'day',
              duration: dayTotal,
              revenueSummary: formatRevenueSummary(aggregateRevenueByCurrency(dayEntriesForSummary))
            }
          });
        }
      }

      // Add week summary if enabled and week changed
      if (exportConfig.summaries.totalWeek && currentWeek && currentWeek !== weekKey) {
        const weekEntriesForSummary = entriesByWeek.get(currentWeek) || [];
        const weekTotal = weekEntriesForSummary.reduce((sum, e) => sum + e.duration, 0);
        if (weekTotal > 0) {
          data.push({
            type: 'summary',
            summary: {
              summaryType: 'week',
              duration: weekTotal,
              revenueSummary: formatRevenueSummary(aggregateRevenueByCurrency(weekEntriesForSummary))
            }
          });
        }
      }

      // Add the actual entry
      data.push({ type: 'entry', entry });

      lastDate = dateKey;
      currentWeek = weekKey;
    }

    // Add final summaries if enabled
    if (exportConfig.summaries.totalDay && lastDate) {
      const dayEntries = entriesByDay.get(lastDate) || [];
      const dayTotal = dayEntries.reduce((sum, e) => sum + e.duration, 0);
      if (dayTotal > 0) {
        data.push({
          type: 'summary',
          summary: {
            summaryType: 'day',
            duration: dayTotal,
            revenueSummary: formatRevenueSummary(aggregateRevenueByCurrency(dayEntries))
          }
        });
      }
    }

    if (exportConfig.summaries.totalWeek && currentWeek) {
      const weekEntries = entriesByWeek.get(currentWeek) || [];
      const weekTotal = weekEntries.reduce((sum, e) => sum + e.duration, 0);
      if (weekTotal > 0) {
        data.push({
          type: 'summary',
          summary: {
            summaryType: 'week',
            duration: weekTotal,
            revenueSummary: formatRevenueSummary(aggregateRevenueByCurrency(weekEntries))
          }
        });
      }
    }

    // Add grand total if enabled
    // Use timeTracking.getCurrentDuration for consistency with statistics calculation
    if (exportConfig.summaries.total) {
      const grandTotal = filteredEntries.reduce((sum, entry) => {
        return sum + timeTracking.getCurrentDuration(entry);
      }, 0);

      if (grandTotal > 0) {
        data.push({
          type: 'summary',
          summary: {
            summaryType: 'grand',
            duration: grandTotal,
            revenueSummary: formatRevenueSummary(aggregateRevenueByCurrency(taskBasedExportEntries))
          }
        });
      }
    }

    return data;
  }, [filteredEntries, exportConfig.summaries, timeTracking, getWeekKey, getDateKeyInTimezone, getEffectiveTimezone, taskBasedExportEntries]);

  // Handle export
  const handleExport = async () => {
    if (!filteredEntries.length) {
      alert('No entries to export for the selected filters')
      return
    }

    setIsExporting(true)
    try {
      // Prepare day-based entries for server if in day-based mode
      const dayBasedExportEntries = exportMode === 'day-based' && dayBasedEntries.length > 0
        ? dayBasedEntries.map(entry => {
            const rowKey = `${entry.dateKey}|${entry.projectId ?? 'no-project'}`
            const budgetInfo = budgetRowStatusDay.get(rowKey)
            return {
              dateKey: entry.dateKey,
              projectId: entry.projectId,
              projectName: entry.projectName,
              location: entry.location,
              description: entry.description,
              startTime: entry.startTime,
              endTime: entry.endTime,
              duration: entry.duration,
              pause: entry.pause,
              timezone: entry.timezone,
              hasOverlap: entry.hasOverlap,
              budgetStatus: budgetInfo?.status, // Budget violation status for Excel highlighting
              rate: entry.rate,
              revenue: entry.revenue,
              currency: entry.currency
            }
          })
        : undefined;

      // Prepare task-based entries for server if in task-based mode
      // This ensures WYSIWYG consistency between preview and export
      const taskBasedExportData = exportMode === 'task-based' && taskBasedExportEntries.length > 0
        ? taskBasedExportEntries.map(entry => ({
            ...entry,
            budgetStatus: budgetRowStatusTask.get(entry.id)?.status // Budget violation status for Excel highlighting
          }))
        : undefined;

      // Add pause column to config for day-based mode
      const exportConfigWithPause = exportMode === 'day-based'
        ? {
            ...exportConfig,
            columns: {
              ...exportConfig.columns,
              pause: true // Always include pause for day-based export
            }
          }
        : exportConfig;

      const blob = await reportsService.exportReport({
        entries: filteredEntries, // Still sent for backwards compatibility and validation
        config: exportConfigWithPause,
        dateRange,
        filters: {
          projectId: selectedProjectIds.length === 1 ? selectedProjectIds[0] : undefined,
          projectIds: selectedProjectIds.length > 1 ? selectedProjectIds : undefined,
          location: selectedLocation || ''
        },
        exportTimezone,
        dateLocale,
        exportLanguage,
        exportMode,
        taskBasedEntries: taskBasedExportData,
        dayBasedEntries: dayBasedExportEntries,
        displaySettings: {
          showOverlapIndicator: displaySettings.showOverlapIndicator,
          showBudgetWarnings: displaySettings.showBudgetWarnings,
          overlapIndicatorColor: displaySettings.overlapIndicatorColor,
          budgetWarningColor: displaySettings.budgetWarningColor,
        },
        overlappingEntryIds: Array.from(overlappingEntryIds), // Legacy: kept for backwards compatibility
        weekStartsOn: effectiveWeekStartDay, // Pass week start preference for server-side week calculations
        sort: effectiveSort // Pass sort configuration for consistent Excel output
      })

      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `time-report-${dateRange.startDate}-to-${dateRange.endDate}.xlsx`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Export error:', error)
      alert('Failed to export report. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  if (isLoading || projects.loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div data-testid="loading-spinner" className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="text-red-600">Error: {error}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Filters & Export Configuration Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {/* Filters Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Date Range */}
          <div>
            <DatePickerDropdown
              id="start-date"
              label="Start Date"
              selectedDate={getDateFromString(dateRange.startDate)}
              onDateChange={(date: Date) => {
                // Prevent future dates
                const today = new Date();
                today.setHours(23, 59, 59, 999); // End of today
                if (date > today) return;

                const newStartDate = getStringFromDate(date);
                setDateRange(prev => {
                  // If new start date is after current end date, adjust end date
                  if (newStartDate > prev.endDate) {
                    return {
                      startDate: newStartDate,
                      endDate: newStartDate // Set end date to same as start date
                    };
                  }
                  return { ...prev, startDate: newStartDate };
                });
              }}
              calendarView="day"
              minWidth="min-w-[200px]"
              weekStartsOn={effectiveWeekStartDay}
            />
          </div>

          <div>
            <DatePickerDropdown
              id="end-date"
              label="End Date"
              selectedDate={getDateFromString(dateRange.endDate)}
              onDateChange={(date: Date) => {
                // Prevent future dates
                const today = new Date();
                today.setHours(23, 59, 59, 999); // End of today
                if (date > today) return;

                const newEndDate = getStringFromDate(date);
                setDateRange(prev => {
                  // If new end date is before current start date, adjust start date
                  if (newEndDate < prev.startDate) {
                    return {
                      startDate: newEndDate, // Set start date to same as end date
                      endDate: newEndDate
                    };
                  }
                  return { ...prev, endDate: newEndDate };
                });
              }}
              calendarView="day"
              minWidth="min-w-[200px]"
              weekStartsOn={effectiveWeekStartDay}
            />
          </div>

          {/* Project Filter */}
          <CustomDropdown
            id="project"
            label="Projects"
            placeholder="All Projects"
            options={activeProjects.map(project => ({ value: project.id, label: project.name }))}
            selectedValues={selectedProjectIds}
            onSelectionChange={setSelectedProjectIds}
            multiSelect={true}
            showClearAll={true}
            clearAllText="Clear All"
          />

          {/* Location Filter */}
          <CustomDropdown
            id="location"
            label="Location"
            placeholder={!selectedProjectIds.length ? "Select a project first" : "All Locations"}
            options={[
              { value: '', label: 'All Locations' },
              ...availableLocations.map(location => ({ value: location, label: location }))
            ]}
            selectedValues={selectedLocation ? [selectedLocation] : []}
            onSelectionChange={(values) => setSelectedLocation(values[0] || '')}
            multiSelect={false}
            disabled={!selectedProjectIds.length}
          />
        </div>

        {/* Activity Overview Chart */}
        <div className="bg-gray-50 p-3 rounded-md">
          <ReportingDistributionChart
            allEntries={chartEntries}
            allProjects={projects.allProjects || []}
            dateRange={dateRange}
          />
        </div>

        {/* Export Options Row */}
        <div className="space-y-4 mt-4">
          {/* Export Mode Selection */}
          <div className="bg-gray-50 p-4 rounded-md">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-md font-medium text-gray-900">Export Mode</h3>
                  <div className="flex items-center text-sm">
                    <button
                      onClick={() => setExportLanguage('en')}
                      className={exportLanguage === 'en' ? 'font-semibold text-gray-900' : 'text-gray-400 hover:text-gray-600'}
                    >EN</button>
                    <span className="mx-1 text-gray-300">/</span>
                    <button
                      onClick={() => setExportLanguage('de')}
                      className={exportLanguage === 'de' ? 'font-semibold text-gray-900' : 'text-gray-400 hover:text-gray-600'}
                    >DE</button>
                  </div>
                </div>
                <div className="flex gap-6">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="exportMode"
                      value="task-based"
                      checked={exportMode === 'task-based'}
                      onChange={() => setExportMode('task-based')}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Task-based</span>
                    <span className="ml-2 text-xs text-gray-500">(one row per time entry)</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="exportMode"
                      value="day-based"
                      checked={exportMode === 'day-based'}
                      onChange={() => setExportMode('day-based')}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Day-based</span>
                    <span className="ml-2 text-xs text-gray-500">(entries aggregated per day/project)</span>
                  </label>
                </div>
              </div>
              <button
                onClick={handleExport}
                disabled={isExporting || !filteredEntries.length}
                className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isExporting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Exporting...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Export to Excel</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Collapsible Options Row */}
          <div className="flex flex-wrap gap-3">
            {/* Column Selection - Collapsible */}
            <div className="bg-gray-50 p-3 rounded-md flex-1 min-w-fit">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setIsColumnsExpanded(!isColumnsExpanded)}
            >
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                <span className="text-sm text-gray-700">
                  Columns: {Object.entries(exportConfig.columns).filter(([key, value]) => {
                    // Don't count pause column if not in day-based mode
                    if (key === 'pause' && exportMode !== 'day-based') return false;
                    return value;
                  }).length} selected
                </span>
              </div>
              <svg className={`w-4 h-4 text-gray-500 transition-transform ${isColumnsExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            {isColumnsExpanded && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {([
                    'day', 'date', 'startTime', 'endTime', 'duration',
                    ...(exportMode === 'day-based' ? ['pause'] : []),
                    'rate', 'revenue', 'project', 'location', 'description', 'originalTimezone'
                  ] as (keyof typeof colLabels)[]).map((key) => (
                    <label key={key} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={!!exportConfig.columns[key as keyof typeof exportConfig.columns]}
                        onChange={(e) => setExportConfig(prev => ({
                          ...prev,
                          columns: {
                            ...prev.columns,
                            [key]: e.target.checked
                          }
                        }))}
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">{colLabels[key]}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

            {/* Summary Options - Collapsible */}
            <div className="bg-gray-50 p-3 rounded-md flex-1 min-w-fit">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
            >
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-sm text-gray-700">
                  Summaries: {(() => {
                    const selected = [];
                    if (exportConfig.summaries.totalDay) selected.push('Day');
                    if (exportConfig.summaries.totalWeek) selected.push('Week');
                    if (exportConfig.summaries.total) selected.push('Total');
                    return selected.length === 0 ? 'none' : selected.join(', ');
                  })()}
                </span>
              </div>
              <svg className={`w-4 h-4 text-gray-500 transition-transform ${isSummaryExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            {isSummaryExpanded && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportConfig.summaries.totalDay}
                      onChange={(e) => setExportConfig(prev => ({
                        ...prev,
                        summaries: {
                          ...prev.summaries,
                          totalDay: e.target.checked
                        }
                      }))}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Total Day - after each day with total hours</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportConfig.summaries.totalWeek}
                      onChange={(e) => setExportConfig(prev => ({
                        ...prev,
                        summaries: {
                          ...prev.summaries,
                          totalWeek: e.target.checked
                        }
                      }))}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Total Week - after each week with total hours</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={exportConfig.summaries.total}
                      onChange={(e) => setExportConfig(prev => ({
                        ...prev,
                        summaries: {
                          ...prev.summaries,
                          total: e.target.checked
                        }
                      }))}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Total - sum of all hours</span>
                  </label>
                </div>
              </div>
            )}
          </div>

            {/* Export Timezone Selection - Collapsible */}
            <div className="bg-gray-50 p-3 rounded-md flex-1 min-w-fit">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setIsTimezoneExpanded(!isTimezoneExpanded)}
            >
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                <span className="text-sm text-gray-700">
                  Timezone: {exportTimezone === PRESERVE_ORIGINAL
                    ? 'Original timezones'
                    : timezoneOptions.find(tz => tz.value === exportTimezone)?.label || exportTimezone.replace(/_/g, ' ')}
                </span>
              </div>
              <svg
                className={`w-4 h-4 text-gray-500 transition-transform ${isTimezoneExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            {isTimezoneExpanded && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-sm text-gray-500 mb-3">
                  {exportTimezone === PRESERVE_ORIGINAL
                    ? 'Times are shown in their original recorded timezone (no conversion).'
                    : 'All times in the preview and export will be converted to this timezone.'}
                </p>
                <CustomDropdown
                  id="export-timezone"
                  label=""
                  placeholder="Select timezone"
                  options={timezoneOptions}
                  selectedValues={[exportTimezone]}
                  onSelectionChange={(values) => setExportTimezone(values[0] || browserTimezone)}
                  multiSelect={false}
                  searchable={true}
                  searchPlaceholder="Search timezones..."
                  showGroups={true}
                />
              </div>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">

        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 opacity-60">{statistics.totalEntries}</div>
            <div className="text-sm text-gray-600">{statistics.displayLabel || 'Total Entries'}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 opacity-60">{formatDuration(statistics.totalDuration)}</div>
            <div className="text-sm text-gray-600">Total Hours</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600 opacity-60">{formatDuration(statistics.averageHoursPerDay * 3600)}</div>
            <div className="text-sm text-gray-600">Avg Hours/Day</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600 opacity-60">{statistics.daysWithEntries}</div>
            <div className="text-sm text-gray-600">Days with Entries</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{(() => {
              // Check if running entry matches current filters
              if (!timeTracking.runningEntry) return 0;
              
              const runningEntry = timeTracking.runningEntry;
              const entryDate = new Date(runningEntry.startTime);
              
              // Check date range filter
              const startDate = new Date(dateRange.startDate);
              const endDate = new Date(dateRange.endDate + 'T23:59:59.999Z'); // End of day
              
              if (entryDate < startDate || entryDate > endDate) {
                return 0; // Running entry is outside date range
              }
              
              // Check project filter
              if (selectedProjectIds.length > 0 && runningEntry.projectId) {
                if (!selectedProjectIds.includes(runningEntry.projectId)) {
                  return 0; // Running entry project doesn't match filter
                }
              } else if (selectedProjectIds.length > 0 && !runningEntry.projectId) {
                return 0; // Filter has projects selected but entry has no project
              }
              
              // Check location filter
              if (selectedLocation && selectedLocation !== '') {
                if (runningEntry.location !== selectedLocation) {
                  return 0; // Running entry location doesn't match filter
                }
              }
              
              // Running entry matches all filters
              return 1;
            })()}</div>
            <div className="text-sm text-gray-600">Active Entries</div>
          </div>
        </div>
      </div>

      {/* Preview Section */}
      {filteredEntries.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {exportMode === 'day-based' ? (() => {
              const uniqueDays = new Set(dayBasedEntries.map(e => e.dateKey)).size;
              const uniqueProjects = new Set(dayBasedEntries.map(e => e.projectId)).size;
              return `Day Preview (${uniqueDays} day${uniqueDays !== 1 ? 's' : ''} / ${uniqueProjects} project${uniqueProjects !== 1 ? 's' : ''})`;
            })() : (() => {
              const taskCount = filteredEntries.length;
              const uniqueProjects = new Set(filteredEntries.map(e => e.projectId)).size;
              return `Task Preview (${taskCount} task${taskCount !== 1 ? 's' : ''} / ${uniqueProjects} project${uniqueProjects !== 1 ? 's' : ''})`;
            })()}
          </h2>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {exportConfig.columns.day && (
                    <SortableTableHeader
                      column="date"
                      label={colLabels.day}
                      currentSort={effectiveSort}
                      onSort={handleSort}
                    />
                  )}
                  {exportConfig.columns.date && (
                    <SortableTableHeader
                      column="date"
                      label={colLabels.date}
                      currentSort={effectiveSort}
                      onSort={handleSort}
                      className="w-16 px-3"
                    />
                  )}
                  {exportConfig.columns.startTime && (
                    <SortableTableHeader
                      column="startTime"
                      label={colLabels.startTime}
                      currentSort={effectiveSort}
                      onSort={handleSort}
                    />
                  )}
                  {exportConfig.columns.endTime && (
                    <SortableTableHeader
                      column="endTime"
                      label={colLabels.endTime}
                      currentSort={effectiveSort}
                      onSort={handleSort}
                    />
                  )}
                  {exportConfig.columns.duration && (
                    <SortableTableHeader
                      column="duration"
                      label={colLabels.duration}
                      currentSort={effectiveSort}
                      onSort={handleSort}
                    />
                  )}
                  {exportMode === 'day-based' && exportConfig.columns.pause && (
                    <SortableTableHeader
                      column="pause"
                      label={colLabels.pause}
                      currentSort={effectiveSort}
                      onSort={handleSort}
                    />
                  )}
                  {exportConfig.columns.rate && (
                    <SortableTableHeader
                      column="rate"
                      label={colLabels.rate}
                      currentSort={effectiveSort}
                      onSort={handleSort}
                    />
                  )}
                  {exportConfig.columns.revenue && (
                    <SortableTableHeader
                      column="revenue"
                      label={colLabels.revenue}
                      currentSort={effectiveSort}
                      onSort={handleSort}
                    />
                  )}
                  {exportConfig.columns.project && (
                    <SortableTableHeader
                      column="project"
                      label={colLabels.project}
                      currentSort={effectiveSort}
                      onSort={handleSort}
                    />
                  )}
                  {exportConfig.columns.location && (
                    <SortableTableHeader
                      column="location"
                      label={colLabels.location}
                      currentSort={effectiveSort}
                      onSort={handleSort}
                    />
                  )}
                  {exportConfig.columns.description && (
                    <SortableTableHeader
                      column="description"
                      label={colLabels.description}
                      currentSort={effectiveSort}
                      onSort={handleSort}
                    />
                  )}
                  {exportConfig.columns.originalTimezone && (
                    <SortableTableHeader
                      column="timezone"
                      label={colLabels.originalTimezone}
                      currentSort={effectiveSort}
                      onSort={handleSort}
                    />
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {exportMode === 'day-based' ? (
                  // Day-based preview with summaries
                  dayBasedPreviewData.map((item, index) => {
                    if (item.type === 'summary') {
                      const isGrandTotal = item.summary.summaryType === 'grand';
                      const isWeekTotal = item.summary.summaryType === 'week';
                      const summaryLabel = isGrandTotal ? 'Total:' : isWeekTotal ? 'Total Week:' : 'Total Day:';
                      return (
                        <tr key={`day-summary-${index}`} className={isGrandTotal ? "bg-gray-300" : isWeekTotal ? "bg-gray-100" : "bg-gray-50"} aria-label={summaryLabel}>
                          {exportConfig.columns.day && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"></td>
                          )}
                          {exportConfig.columns.date && (
                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900"></td>
                          )}
                          {exportConfig.columns.startTime && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"></td>
                          )}
                          {exportConfig.columns.endTime && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"></td>
                          )}
                          {exportConfig.columns.duration && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                              {formatDuration(item.summary.duration)}
                            </td>
                          )}
                          {exportConfig.columns.pause && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                              {formatDuration(item.summary.pause)}
                            </td>
                          )}
                          {exportConfig.columns.rate && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"></td>
                          )}
                          {exportConfig.columns.revenue && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium text-right">
                              {item.summary.revenueSummary}
                            </td>
                          )}
                          {exportConfig.columns.project && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"></td>
                          )}
                          {exportConfig.columns.location && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"></td>
                          )}
                          {exportConfig.columns.description && (
                            <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                              <span className="sr-only">
                                {item.summary.summaryType === 'grand' ? 'Total:' : item.summary.summaryType === 'week' ? 'Total Week:' : 'Total Day:'}
                              </span>
                            </td>
                          )}
                          {exportConfig.columns.originalTimezone && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"></td>
                          )}
                        </tr>
                      );
                    }

                    // Entry row
                    const entry = item.entry;
                    const start = new Date(entry.startTime);
                    const end = new Date(entry.endTime);

                    // Determine which timezone to use for display
                    const displayTimezone = exportTimezone === PRESERVE_ORIGINAL
                      ? (entry.timezone || 'Europe/Berlin')
                      : exportTimezone;

                    const hasOverlapHighlight = displaySettings.showOverlapIndicator && entry.hasOverlap;
                    const dayRowKey = `${entry.dateKey}|${entry.projectId ?? 'no-project'}`
                    const dayBudgetInfo = budgetRowStatus.get(dayRowKey)
                    const hasBudgetHighlight = !!dayBudgetInfo && dayBudgetInfo.status !== 'in'

                    return (
                      <tr
                        key={`day-${entry.dateKey}-${entry.projectId || 'no-project'}-${index}`}
                        style={{
                          backgroundColor: hasOverlapHighlight
                            ? displaySettings.overlapIndicatorColor
                            : hasBudgetHighlight
                              ? displaySettings.budgetWarningColor
                              : undefined
                        }}
                        title={[
                          hasOverlapHighlight ? "This day has overlapping entries - duration adjusted to avoid double-counting" : null,
                          hasBudgetHighlight ? dayBudgetInfo.tooltipText : null,
                        ].filter(Boolean).join('\n') || undefined}
                      >
                        {exportConfig.columns.day && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className="inline-flex items-center gap-1">
                              <span>{formatDayInTimezone(start, displayTimezone)}</span>
                              {/* Overlap warning icon */}
                              {displaySettings.showOverlapIndicator && entry.hasOverlap && (
                                <span
                                  className="inline-flex items-center justify-center w-3.5 h-3.5"
                                  style={{ color: "#ef4444" }}
                                  title="This day has overlapping entries - duration adjusted to avoid double-counting"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              )}
                              {/* Budget violation icon */}
                              {hasBudgetHighlight && (
                                <span
                                  className="inline-flex items-center justify-center w-3.5 h-3.5"
                                  style={{ color: "#ef4444" }}
                                  title={dayBudgetInfo?.tooltipText || "Out of budget"}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                                    <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              )}
                              {/* Timezone indicator icon */}
                              {showTimezoneColumn && entry.hasDifferentTimezone && (
                                <TimezoneIndicator
                                  entryTimezone={entry.timezone}
                                  showIndicator={true}
                                  colorClass="text-blue-600"
                                />
                              )}
                            </span>
                          </td>
                        )}
                        {exportConfig.columns.date && (
                          <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDateNumberInTimezone(start, displayTimezone)}
                          </td>
                        )}
                        {exportConfig.columns.startTime && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatTimeInTimezone(start, displayTimezone)}
                          </td>
                        )}
                        {exportConfig.columns.endTime && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatTimeInTimezone(end, displayTimezone)}
                          </td>
                        )}
                        {exportConfig.columns.duration && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDuration(entry.duration)}
                          </td>
                        )}
                        {exportConfig.columns.pause && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDuration(entry.pause)}
                          </td>
                        )}
                        {exportConfig.columns.rate && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            {entry.rate !== null && entry.currency
                              ? `${formatRate(entry.rate, entry.currency)}/h`
                              : ''}
                          </td>
                        )}
                        {exportConfig.columns.revenue && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            {entry.revenue !== null && entry.currency
                              ? formatRate(entry.revenue, entry.currency)
                              : ''}
                          </td>
                        )}
                        {exportConfig.columns.project && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {entry.projectName}
                          </td>
                        )}
                        {exportConfig.columns.location && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {entry.location}
                          </td>
                        )}
                        {exportConfig.columns.description && (
                          <td className="px-6 py-4 text-sm text-gray-900 truncate max-w-xs" title={entry.description}>
                            {entry.description}
                          </td>
                        )}
                        {exportConfig.columns.originalTimezone && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {(entry.timezone || 'Europe/Berlin').replace(/_/g, ' ')}
                          </td>
                        )}
                      </tr>
                    );
                  })
                ) : (
                  // Task-based preview (original)
                  previewData.map((item, index) => {
                    if (item.type === 'summary') {
                      const isGrandTotal = item.summary!.summaryType === 'grand';
                      const summaryLabel = isGrandTotal ? 'Total:' : 'Total Day:';
                      return (
                        <tr key={`summary-${index}`} className={isGrandTotal ? "bg-gray-300" : "bg-gray-50"} aria-label={summaryLabel}>
                          {exportConfig.columns.day && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"></td>
                          )}
                          {exportConfig.columns.date && (
                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900"></td>
                          )}
                          {exportConfig.columns.startTime && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"></td>
                          )}
                          {exportConfig.columns.endTime && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"></td>
                          )}
                          {exportConfig.columns.duration && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                              {formatDuration(item.summary!.duration)}
                            </td>
                          )}
                          {exportConfig.columns.rate && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"></td>
                          )}
                          {exportConfig.columns.revenue && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium text-right">
                              {item.summary!.revenueSummary}
                            </td>
                          )}
                          {exportConfig.columns.project && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"></td>
                          )}
                          {exportConfig.columns.location && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"></td>
                          )}
                          {exportConfig.columns.description && (
                            <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                              <span className="sr-only">
                                {item.summary!.summaryType === 'grand' ? 'Total:' : item.summary!.summaryType === 'week' ? 'Total Week:' : 'Total Day:'}
                              </span>
                            </td>
                          )}
                          {exportConfig.columns.originalTimezone && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"></td>
                          )}
                        </tr>
                      )
                    }

                    if (item.type === 'entry' && item.entry) {
                      const entry = item.entry;
                      const start = new Date(entry.startTime)
                      const end = entry.endTime ? new Date(entry.endTime) : null
                      const duration = timeTracking.getCurrentDuration(entry)
                      const project = entry.projectId ? projects.projects.find(p => p.id === entry.projectId) : null
                      const taskEntry = taskBasedExportEntries.find(e => e.id === entry.id)

                      // Determine which timezone to use for display
                      const displayTimezone = exportTimezone === PRESERVE_ORIGINAL
                        ? (entry.timezone || 'Europe/Berlin')
                        : exportTimezone

                      const hasOverlapHighlight = displaySettings.showOverlapIndicator && overlappingEntryIds.has(entry.id);
                      const taskBudgetInfo = budgetRowStatus.get(entry.id)
                      const hasBudgetHighlight = !!taskBudgetInfo && taskBudgetInfo.status !== 'in'

                      return (
                        <tr
                          key={entry.id}
                          style={{
                            backgroundColor: hasOverlapHighlight
                              ? displaySettings.overlapIndicatorColor
                              : hasBudgetHighlight
                                ? displaySettings.budgetWarningColor
                                : undefined
                          }}
                          title={[
                            hasOverlapHighlight ? "This entry overlaps with another entry in the same project" : null,
                            hasBudgetHighlight ? taskBudgetInfo.tooltipText : null,
                          ].filter(Boolean).join('\n') || undefined}
                        >
                          {exportConfig.columns.day && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <span className="inline-flex items-center gap-1">
                                <span>{formatDayInTimezone(start, displayTimezone)}</span>
                                {/* Overlap warning icon */}
                                {displaySettings.showOverlapIndicator && overlappingEntryIds.has(entry.id) && (
                                  <span
                                    className="inline-flex items-center justify-center w-3.5 h-3.5"
                                    style={{ color: "#ef4444" }}
                                    title="This entry overlaps with another entry in the same project"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                  </span>
                                )}
                                {/* Budget violation icon */}
                                {hasBudgetHighlight && (
                                  <span
                                    className="inline-flex items-center justify-center w-3.5 h-3.5"
                                    style={{ color: "#ef4444" }}
                                    title={taskBudgetInfo?.tooltipText || "Out of budget"}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                                      <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                                    <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                                    </svg>
                                  </span>
                                )}
                                {/* Timezone indicator icon */}
                                {showTimezoneColumn && (
                                  <TimezoneIndicator
                                    entryTimezone={entry.timezone}
                                    showIndicator={true}
                                    colorClass="text-blue-600"
                                  />
                                )}
                              </span>
                            </td>
                          )}
                          {exportConfig.columns.date && (
                            <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDateNumberInTimezone(start, displayTimezone)}
                            </td>
                          )}
                          {exportConfig.columns.startTime && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatTimeInTimezone(start, displayTimezone)}
                            </td>
                          )}
                          {exportConfig.columns.endTime && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {end ? formatTimeInTimezone(end, displayTimezone) : '-'}
                            </td>
                          )}
                          {exportConfig.columns.duration && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDuration(duration)}
                            </td>
                          )}
                          {exportConfig.columns.rate && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              {taskEntry?.rate !== null && taskEntry?.rate !== undefined && taskEntry?.currency
                                ? `${formatRate(taskEntry.rate, taskEntry.currency)}/h`
                                : ''}
                            </td>
                          )}
                          {exportConfig.columns.revenue && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                              {taskEntry?.revenue !== null && taskEntry?.revenue !== undefined && taskEntry?.currency
                                ? formatRate(taskEntry.revenue, taskEntry.currency)
                                : ''}
                            </td>
                          )}
                          {exportConfig.columns.project && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {project ? project.name : '-'}
                            </td>
                          )}
                          {exportConfig.columns.location && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {entry.location || '-'}
                            </td>
                          )}
                          {exportConfig.columns.description && (
                            <td className="px-6 py-4 text-sm text-gray-900 truncate max-w-xs">
                              {entry.description}
                            </td>
                          )}
                          {exportConfig.columns.originalTimezone && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {(entry.timezone || 'Europe/Berlin').replace(/_/g, ' ')}
                            </td>
                          )}
                        </tr>
                      )
                    }

                    return null;
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-center text-sm text-gray-500">
            {exportMode === 'day-based'
              ? `Showing ${dayBasedEntries.length} aggregated rows (from ${filteredEntries.length} entries)`
              : `Showing ${filteredEntries.length} total entries`
            }
          </div>
        </div>
      )}
    </div>
  )
} 